import React from "react";
import {
 Card,
 H1,
 H3,
 Button,
 Tag,
 Spinner,
 NonIdealState,
 Icon,
 InputGroup,
} from "@blueprintjs/core";
import { DateInput } from "@blueprintjs/datetime";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import moment from "moment";
import { inject, observer } from "mobx-react";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import { withRouter } from "react-router-dom";
import {
    getStatusMeta as getStatusMetaGlobal,
    getDisplayStatusMeta as getDisplayStatusMetaGlobal,
    toTitleCase as toTitleCaseGlobal,
} from "../../constants/statusColors";
import { getIsBilledLabel, getIsBilledPill } from "../../utils/isBilledPill";

class BookingGivenReport extends React.Component {
 state = {
  branches: [],
  doctors: [],
  selectedBranch: "",
  selectedDoctor: "",
  selectedBranchName: null,
  selectedDoctorName: null,
  fromDate: new Date(),
  toDate: new Date(),
  isLoading: false,
  isGenerating: false,
  rows: [],
  allRows: [], // All rows data for client-side filtering
  hasGenerated: false,
  summary: [],
  totalCount: 0,
  gridApi: null,
  columnApi: null,
  quickFilterText: "",
 };

 async componentDidMount() {
  await this.loadBranches();
  await this.loadDoctors();
  // Automatically generate report on load
  await this.generateReport();
 }

 componentDidUpdate(prevProps) {
  if (
   prevProps.authStore?.selectedBranchName !==
   this.props.authStore?.selectedBranchName
  ) {
   const preferredName = this.props.authStore?.selectedBranchName;
   if (!preferredName) {
    this.setState({ selectedBranch: "", selectedBranchName: null });
    return;
   }
   const norm = (value) =>
    String(value || "")
     .trim()
     .toLowerCase();
   const match = this.state.branches.find(
    (branch) => norm(branch.name) === norm(preferredName)
   );
   if (match && match._id !== this.state.selectedBranch) {
    this.setState({
     selectedBranch: match._id,
     selectedBranchName: match.name,
    });
   }
  }
 }

 loadBranches = async () => {
  try {
   this.setState({ isLoading: true });
   const response = await apiCallPost(
    `/rai/${this.props.authStore.prefix}/getAllSites`,
    {}
   );
   const rawBranches = response?.data || response || [];
   const branches = (Array.isArray(rawBranches) ? rawBranches : []).map(
    (site, idx) => ({
     _id:
      site?._id ||
      site?.id ||
      site?.siteId ||
      site?.value ||
      site?.key ||
      (typeof site === "string" ? site : String(idx)),
     name:
      site?.site ||
      site?.siteName ||
      site?.name ||
      site?.branchName ||
      site?.label ||
      (typeof site === "string" ? site : `Site ${idx + 1}`),
    })
   );
   const sorted = branches.sort((a, b) =>
    (a.name || "").localeCompare(b.name || "")
   );
   const preferredName = this.props.authStore?.selectedBranchName;
   let nextState = { branches: sorted };
   if (preferredName) {
    const norm = (value) =>
     String(value || "")
      .trim()
      .toLowerCase();
    const match = sorted.find(
     (branch) => norm(branch.name) === norm(preferredName)
    );
    if (match) {
     nextState = {
      ...nextState,
      selectedBranch: match._id,
      selectedBranchName: match.name,
     };
    }
   }
   this.setState(nextState);
  } catch (err) {
   console.error("Failed to load branches", err);
   this.setState({
    branches: [
     { _id: "branch1", name: "Barrackpore" },
     { _id: "branch2", name: "Kolkata" },
    ],
   });
  } finally {
   this.setState({ isLoading: false });
  }
 };

 loadDoctors = async () => {
  try {
   // Use the existing doctor/consultant API
   const response = await apiCallPost(
    `/rai/${this.props.authStore.prefix}/getAllConsultants`,
    {}
   );

   // Transform consultants to doctor format
   const doctors = Array.isArray(response)
    ? response.map((consultant) => ({
       _id: consultant._id || consultant.id,
       name: consultant.name || consultant.consultantName,
       specialization: consultant.specialization || consultant.department,
       phone: consultant.phone || consultant.contactNumber,
       email: consultant.email || consultant.emailId,
       shortcode: consultant.shortcode || consultant.code,
      }))
    : [];

   console.log("🔍 DEBUG: Doctors/Consultants loaded:", doctors.length);
   this.setState({ doctors });
   return doctors;
  } catch (err) {
   console.error("Error loading doctors:", err);
   // Try alternative API endpoint
   try {
    const response = await apiCallPost(
     `/meta-sx/${this.props.authStore.prefix}/doctorMaster/getAll`,
     {}
    );
    const doctors = response?.data || response || [];
    this.setState({ doctors });
    return doctors;
   } catch (fallbackErr) {
    console.error("Fallback doctor API also failed:", fallbackErr);
    // Return dummy doctors if both APIs fail
    const dummyDoctors = [
     { _id: "doc1", name: "DR. ARIJIT MITRA" },
     { _id: "doc2", name: "DR. ARNAB DAS" },
     { _id: "doc3", name: "DR. SUDIPTA ROY" },
     { _id: "doc4", name: "DR. PRITAM SARKAR" },
    ];
    this.setState({ doctors: dummyDoctors });
    return dummyDoctors;
   }
  }
 };

 handleDateChange = (field, value) => {
  this.setState({ [field]: value });
 };

 handleBranchFilterSelect = (value) => {
  if (!value) {
   this.setState(
    {
     selectedBranch: "",
     selectedBranchName: null,
    },
    () => {
     // Apply client-side filters after clearing branch filter
     if (this.state.allRows.length > 0) {
      this.applyClientSideFilters();
     }
    }
   );
   return;
  }
  const match = this.state.branches.find((b) => b.name === value);
  if (match) {
   this.setState(
    {
     selectedBranch: match._id,
     selectedBranchName: match.name,
    },
    () => {
     // Apply client-side filters after setting branch filter
     if (this.state.allRows.length > 0) {
      this.applyClientSideFilters();
     }
    }
   );
  }
 };

 // Helper function to create branch items for dropdown
 createBranchSelectItems = () => {
  return this.state.branches.map((branch) => branch.name).filter(Boolean);
 };

 // Helper function to create doctor items for dropdown with shortcode formatting
 createDoctorSelectItems = () => {
  return this.state.doctors
   .map((doctor) => {
    const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
    const name = doctor.name;
    return `${shortcode}${name}`;
   })
   .filter(Boolean);
 };

 // Helper function to get formatted doctor name
 getFormattedDoctorName = (doctor) => {
  if (!doctor) return null;
  const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
  const name = doctor.name;
  return `${shortcode}${name}`;
 };

 // Helper function to extract doctor name from formatted string
 extractDoctorNameFromFormatted = (formattedName) => {
  if (!formattedName) return null;
  // Remove shortcode part (everything before " | ")
  const withoutShortcode = formattedName.includes(" | ")
   ? formattedName.split(" | ")[1]
   : formattedName;
  return withoutShortcode.trim();
 };

 // Apply client-side filtering to existing data
 applyClientSideFilters = () => {
  let filteredRows = [...this.state.allRows];

  // Apply doctor filter if selected
  if (this.state.selectedDoctor && this.state.selectedDoctorName) {
   const extractedDoctorName = this.extractDoctorNameFromFormatted(
    this.state.selectedDoctorName
   );
   console.log(
    "🔍 DEBUG: Applying client-side doctor filter:",
    extractedDoctorName
   );

   filteredRows = filteredRows.filter((row) => {
    const rowDoctorName = row.doctorName || "";
    // Try multiple matching strategies
    const matches = [
     // Exact match
     rowDoctorName === extractedDoctorName,
     // Match without "DR. " prefix
     rowDoctorName.replace(/^DR\.\s*/i, "") ===
      extractedDoctorName.replace(/^DR\.\s*/i, ""),
     // Case-insensitive match
     rowDoctorName.toLowerCase() === extractedDoctorName.toLowerCase(),
     // Partial match (contains)
     rowDoctorName.toLowerCase().includes(extractedDoctorName.toLowerCase()),
    ].some(Boolean);

    return matches;
   });

   console.log("🔍 DEBUG: Client-side filtering result:", {
    original: this.state.allRows.length,
    filtered: filteredRows.length,
    extractedDoctorName,
   });
  }

  // Update state with filtered data
  this.setState({
   rows: filteredRows,
   summary: this.computeSummary(filteredRows),
   totalCount: filteredRows.length,
  });

  // Show feedback to user
  const filterMsg = this.state.selectedDoctorName
   ? `Filtered to ${
      filteredRows.length
     } bookings for ${this.extractDoctorNameFromFormatted(
      this.state.selectedDoctorName
     )}`
   : `Showing all ${filteredRows.length} bookings`;

  cogoToast.success(filterMsg);
 };

 // Handle doctor selection from SingleSelectWrapper
 handleDoctorFilterSelect = (formattedDoctorName) => {
  // Find the doctor by matching the formatted name
  const selectedDoctor = this.state.doctors.find((doctor) => {
   const formatted = this.getFormattedDoctorName(doctor);
   return formatted === formattedDoctorName;
  });

  this.setState(
   {
    selectedDoctor: selectedDoctor ? selectedDoctor._id : "",
    selectedDoctorName: formattedDoctorName || null,
   },
   () => {
    // Apply filtering immediately if we have data
    if (this.state.allRows.length > 0) {
     this.applyClientSideFilters();
    }
   }
  );
 };

 // Navigate to surgery details page
 navigateToDetails = (surgery) => {
  const detailsPath = `/v3/workflow/surgery-booking/${
   surgery._id || surgery.docNo || surgery.bookingReference
  }`;
  window.open(detailsPath, "_blank", "noopener,noreferrer");
 };

 onGridReady = (params) => {
  this.setState({
   gridApi: params.api,
   columnApi: params.columnApi,
  });
  params.api.sizeColumnsToFit();
 };

 // Get only visible/displayed columns (respects sidebar column selection)
 getDisplayedColumns = () => {
  if (!this.state.columnApi) return this.getColumnDefs();
  const allColumns = this.state.columnApi.getAllColumns();
  if (!allColumns) return this.getColumnDefs();
  const visibleColumns = allColumns.filter((col) => col.isVisible());
  return visibleColumns.map((col) => {
   const colDef = col.getColDef();
   return {
    headerName: colDef.headerName,
    field: colDef.field,
    ...colDef,
   };
  });
 };

 onQuickFilterChanged = (event) => {
  const value = event.target.value;
  this.setState({ quickFilterText: value });
  if (this.state.gridApi) {
   this.state.gridApi.setQuickFilter(value);
  }
 };

 exportToCSV = () => {
  // Fallback to manual CSV export with visible columns
  try {
   // Get filtered and sorted data (fallback to state data if grid API not available)
   let filteredData = this.state.rows;
   if (this.state.gridApi) {
    filteredData = [];
    this.state.gridApi.forEachNodeAfterFilterAndSort((node) => {
     filteredData.push(node.data);
    });
   }

   const selectedBranchName =
    this.state.branches.find((b) => b._id === this.state.selectedBranch)
     ?.name || "All Branches";

   // Get selected doctor name if filter is applied
   const selectedDoctorName = this.state.selectedDoctor
    ? this.state.doctors.find((d) => d._id === this.state.selectedDoctor)
       ?.name || "Unknown Doctor"
    : "All Doctors";

   // Create comprehensive header information
   const headerInfo = [
    [`BOOKING GIVEN REPORT - ${this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}`],
    [""], // Empty row for spacing
    ["Report Details:"],
    [`Branch: ${selectedBranchName}`],
    [`Doctor/Surgeon: ${selectedDoctorName}`],
    [
     `Filter Period: ${moment(this.state.fromDate).format(
      "DD-MMM-YYYY"
     )} to ${moment(this.state.toDate).format("DD-MMM-YYYY")}`,
    ],
    [`Generated At: ${moment().format("DD-MMM-YYYY, HH:mm:ss")}`],
    [`Total Records: ${filteredData.length}`],
    [""], // Empty row for spacing
    ["Data Export:"],
    [""], // Empty row for spacing
   ];

   // Get only visible columns (respects sidebar column selection)
   const columns = this.getDisplayedColumns();

   // Create column headers from visible columns
   const headers = columns.map((col) => col.headerName);

   // Format data rows based on visible columns
   const dataRows = filteredData.map((booking) => {
    return columns.map((col) => {
     const field = col.field;

     // Handle special field mappings
     if (field === "doctorName") {
      return booking.doctorName || "-";
     }
     if (field === "docNo") {
      return booking.docNo || booking.bookingReference || "-";
     }
     if (field === "patientInfo") {
      const pid = booking.patientId || booking.pid || "-";
      const name = booking.patientName || "-";
      return `${pid}/${name}`;
     }
     if (field === "surgeryDate") {
      return booking.surgeryDate
       ? moment(booking.surgeryDate).format("DD/MM/YYYY")
       : "-";
     }
     if (field === "createdAt") {
      return booking.createdAt
       ? moment(booking.createdAt).format("DD/MM/YYYY HH:mm")
       : "-";
     }
        if (field === "status") {
          return this.getDisplayStatusMeta(booking).label;
        }
     if (field === "lens") {
      return booking.lens || "NO LENS";
     }
     if (field === "createdByName") {
      return booking.createdByName || "System";
     }

     // Default: return field value or dash
     return booking[field] || "-";
    });
   });

   // Combine all data
   const csvData = [...headerInfo, headers, ...dataRows];

   // Convert to CSV format
   const csvContent = csvData
    .map((row) =>
     row
      .map((field) => {
       // Escape quotes and wrap in quotes if field contains comma, quote, or newline
       const fieldStr = String(field || "");
       if (
        fieldStr.includes(",") ||
        fieldStr.includes('"') ||
        fieldStr.includes("\n")
       ) {
        return `"${fieldStr.replace(/"/g, '""')}"`;
       }
       return fieldStr;
      })
      .join(",")
    )
    .join("\n");

   // Create and download file
   const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
   const link = document.createElement("a");
   const url = URL.createObjectURL(blob);
   link.setAttribute("href", url);

   const fileName = `Booking_Given_Report_${moment(this.state.fromDate).format(
    "DD-MMM-YYYY"
   )}_to_${moment(this.state.toDate).format(
    "DD-MMM-YYYY"
   )}_Generated_${moment().format("DD-MMM-YYYY_HH-mm")}.csv`;
   link.setAttribute("download", fileName);
   link.style.visibility = "hidden";
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);

   cogoToast.success(
    `CSV exported successfully: ${fileName} (with selected columns)`
   );
  } catch (err) {
   console.error("Error exporting CSV:", err);
   cogoToast.error("Failed to export CSV");
  }
 };

 exportToPDF = () => {
  try {
   // Get filtered and sorted data from AG Grid
   let filteredData = [];
   if (this.state.gridApi) {
    // Get all filtered and sorted rows from AG Grid
    this.state.gridApi.forEachNodeAfterFilterAndSort((node) => {
     filteredData.push(node.data);
    });
   } else {
    // Fallback to state data if grid API not available
    filteredData = this.state.rows;
   }

   // Get only visible columns (respects sidebar column selection)
   const columns = this.getDisplayedColumns();

   const selectedBranchName =
    this.state.branches.find((b) => b._id === this.state.selectedBranch)
     ?.name || "All Branches";
   const selectedDoctorName = this.state.selectedDoctorName
    ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
    : null;

   // Create a new window for printing
   const printWindow = window.open("", "_blank");

   // Generate HTML content for PDF
   const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
     <title>Booking Given Report</title>
     <style>
      /* Ensure landscape orientation when printing */
      @page {
       size: A4 landscape;
       margin: 12mm;
      }
      body { 
       font-family: Arial, sans-serif; 
       margin: 20px; 
       font-size: 12px;
       color: #000;
      }
      .header {
       text-align: center;
       margin-bottom: 30px;
       border-bottom: 2px solid #000;
       padding-bottom: 20px;
      }
      .header h1 {
       margin: 0;
       font-size: 18px;
       font-weight: bold;
      }
      .header p {
       margin: 5px 0;
       font-size: 14px;
      }
      table {
       width: 100%;
       border-collapse: collapse;
       margin-top: 20px;
       font-size: 10px;
      }
      th, td {
       border: 1px solid #000;
       text-align: left;
       vertical-align: top;
      }
      th { 
       padding: 16px 10px;
       text-align: center;
      }
      td { 
       padding: 8px 10px 56px 10px;
       line-height: 1.6; 
      }
      tbody tr { page-break-inside: avoid; }
      th {
       background-color: #f0f0f0;
       font-weight: bold;
       text-align: center;
      }
      .no-break {
       page-break-inside: avoid;
      }
      @media print {
       @page { size: A4 landscape; }
       body { margin: 10px; }
       .header { page-break-after: avoid; }
      }
     </style>
    </head>
    <body>
     <div class="header">
      <h1>BOOKING GIVEN REPORT - ${this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}</h1>
      <p>${selectedBranchName}${
    selectedDoctorName ? ` • ${selectedDoctorName}` : ""
   } - ${moment().format("DD/MM/YYYY, HH:mm")}</p>
      <p>Bookings created from ${moment(this.state.fromDate).format(
       "DD-MMM-YYYY"
      )} to ${moment(this.state.toDate).format("DD-MMM-YYYY")}</p>
      <p>Total Bookings: ${filteredData.length}</p>
     </div>
     
     <table>
      <thead>
       <tr>
        ${columns.map((col) => `<th>${col.headerName}</th>`).join("")}
       </tr>
      </thead>
      <tbody>
       ${filteredData
        .map((booking) => {
         const cells = columns
          .map((col) => {
           const field = col.field;

           // Handle special field mappings
           if (field === "doctorName") {
            return `<td>${booking.doctorName || "-"}</td>`;
           }
           if (field === "docNo") {
            return `<td>${
             booking.docNo || booking.bookingReference || "-"
            }</td>`;
           }
           if (field === "patientInfo") {
            return `<td><strong>${booking.patientId || booking.pid || "-"}/${
             booking.patientName || "-"
            }</strong></td>`;
           }
           if (field === "surgeryDate") {
            return `<td>${
             booking.surgeryDate
              ? moment(booking.surgeryDate).format("DD/MM/YY")
              : "-"
            }</td>`;
           }
           if (field === "createdAt") {
            return `<td>${
             booking.createdAt
              ? moment(booking.createdAt).format("DD/MM/YY HH:mm")
              : "-"
            }</td>`;
           }
           if (field === "status") {
            return `<td>${this.getDisplayStatusMeta(booking).label}</td>`;
           }
           if (field === "eye") {
            return `<td><strong>${booking.eye || "-"}</strong></td>`;
           }
           if (field === "lens") {
            return `<td>${booking.lens || "NO LENS"}</td>`;
           }
           if (field === "createdByName") {
            return `<td>${booking.createdByName || "System"}</td>`;
           }

           // Default: return field value or dash
           return `<td>${booking[field] || "-"}</td>`;
          })
          .join("");
         return `<tr class="no-break">${cells}</tr>`;
        })
        .join("")}
      </tbody>
     </table>
    </body>
    </html>
   `;

   printWindow.document.write(htmlContent);
   printWindow.document.close();

   // Wait for content to load then trigger print
   printWindow.onload = () => {
    printWindow.print();
   };

   cogoToast.success("PDF print dialog opened (with selected columns)");
  } catch (err) {
   console.error("Error exporting PDF:", err);
   cogoToast.error("Failed to export PDF");
  }
 };

 calculateAge = (dob) => {
  if (!dob) return null;
  try {
   const birthDate = new Date(dob);
   if (Number.isNaN(birthDate.getTime())) return null;
   const today = new Date();
   let age = today.getFullYear() - birthDate.getFullYear();
   const monthDiff = today.getMonth() - birthDate.getMonth();
   if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
   ) {
    age -= 1;
   }
   return age;
  } catch (err) {
   console.error("Failed to calculate age", err);
   return null;
  }
 };

 determineLaterality = (selectedSurgeries) => {
  if (!Array.isArray(selectedSurgeries) || selectedSurgeries.length === 0) {
   return "RE";
  }
  const unique = [
   ...new Set(
    selectedSurgeries
     .map((s) => s?.laterality)
     .filter(Boolean)
     .map((lat) => String(lat).trim().toUpperCase())
   ),
  ];
  if (unique.length === 0) return "RE";
  if (unique.length === 1) return unique[0];
  return "BE";
 };

 formatSelectedSurgeries = (selectedSurgeries) => {
  if (!Array.isArray(selectedSurgeries) || selectedSurgeries.length === 0) {
   return null;
  }
  return selectedSurgeries
   .map((surgery) => {
    let display = surgery?.name || surgery?.surgeryName || "Unknown Surgery";
    // if (surgery?.pricing?.surgeryRate) {
    //  display += ` (₹${Number(surgery.pricing.surgeryRate).toLocaleString(
    //   "en-IN"
    //  )})`;
    // }
    return display;
   })
   .join("; ");
 };

 formatSelectedGroup = (selectedSurgeries) => {
  if (!Array.isArray(selectedSurgeries) || selectedSurgeries.length === 0) {
   return null;
  }
  return selectedSurgeries
   .map((surgery) => surgery?.group)
   .filter(Boolean)
   .join(", ");
 };

 formatIOLItems = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
   return null;
  }
  return items
   .map((item) => {
    let label = item?.itemName || item?.name || "Unknown IOL";
    if (item?.selectedVariants && Object.keys(item.selectedVariants).length) {
     const variants = Object.entries(item.selectedVariants)
      .filter(([, value]) => Boolean(value))
      .map(([key, value]) => {
       let variantName = key;
       if (key === "property1" && item?.itemDetails?.variantProperty1Name) {
        variantName = item.itemDetails.variantProperty1Name;
       } else if (
        key === "property2" &&
        item?.itemDetails?.variantProperty2Name
       ) {
        variantName = item.itemDetails.variantProperty2Name;
       } else if (
        key === "property3" &&
        item?.itemDetails?.variantProperty3Name
       ) {
        variantName = item.itemDetails.variantProperty3Name;
       }
       return `${variantName}: ${value}`;
      });
     if (variants.length) {
      label += ` (${variants.join(", ")})`;
     }
    }
    return label;
   })
   .join("; ");
 };

toTitleCase = (value) => toTitleCaseGlobal(value);

getStatusMeta = (status) => getStatusMetaGlobal(status);

getDisplayStatusMeta = (row) => {
    const status = row?.status || "-";
    const payorPanel = row?.payorPanel || row?.payor || row?.patientPanel || "";
    const isBilled = row?.isBilled ?? false;
    return getDisplayStatusMetaGlobal({ status, payorPanel, isBilled });
};

renderStatusCell = (row) => {
    if (!row) return "-";
    const meta = this.getDisplayStatusMeta(row);
    return `<span style="display:inline-flex;align-items:center;justify-content:center;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;color:${meta.color};background:${meta.background};border:1px solid ${meta.border};text-transform:none;line-height:1;">${meta.label}</span>`;
};
renderIsBilledCell = (row) => getIsBilledPill(row);

 mapSurgeryToRow = (surgery, index) => {
  const createdAt = surgery.createdAt ? new Date(surgery.createdAt) : null;
  const scheduledDate = surgery.scheduledDate
   ? new Date(surgery.scheduledDate)
   : null;
  const patientOriginal = surgery.patientOriginalData || {};
  const patientId =
   surgery.patientGenericPid ||
   surgery.patientId ||
   patientOriginal.sysPid ||
   `PID-${String(index + 1).padStart(4, "0")}`;
  const patientName = surgery.patientName || patientOriginal.name || "Unknown";
  const computedAge =
   this.calculateAge(surgery.patientDob) ||
   this.calculateAge(patientOriginal.dob);
  const patientAge =
   typeof surgery.patientAge === "number"
    ? surgery.patientAge
    : computedAge !== null
    ? computedAge
    : "-";
  const patientGender = surgery.patientSex || patientOriginal.sex || "-";
  const eye = this.determineLaterality(surgery.selectedSurgeries);
  const diet = surgery.isDiabetic ? "DIABETIC" : "NON-DIABETIC";
  const lens =
   this.formatIOLItems(surgery.IOLitems) ||
   surgery.iolType ||
   surgery.implantType ||
   (Array.isArray(surgery.IOLitems) && surgery.IOLitems.length
    ? "IOL SELECTED"
    : "NO LENS");
  const surgeryName =
   this.formatSelectedSurgeries(surgery.selectedSurgeries) ||
   surgery.procedure ||
   surgery.surgeryType ||
   surgery.type ||
   "Surgery";
  const surgeryGroup =
   this.formatSelectedGroup(surgery.selectedSurgeries) || "-";
  const remarks = surgery.additionalNotes || surgery.notes || "-";
  const status =
   surgery.status ||
   surgery.workflowStatus ||
   surgery.bookingStatus ||
   "confirmed";

  return {
   _id: surgery._id || surgery.id || `booking_${index}`,
   docNo:
    surgery.bookingReference ||
    surgery._id ||
    surgery.id ||
    `BKG-${String(index + 1).padStart(4, "0")}`,
   bookingReference:
    surgery.bookingReference || surgery.bookingId || `BKG-${index + 1}`,
   patientId,
   pid: patientId,
   patientName,
   branchName: surgery.branchName || surgery.branch?.name || "-",
   doctorName: surgery.doctorName || surgery.doctor?.name || "-",
  status,
  payorPanel: surgery.payorPanel || "",
  patientPanel: surgery.payorPanel || surgery.patientOriginalData?.panel || "Cash",
  isBilled: Boolean(surgery.isBilled),
   createdByName: surgery.createdByName || surgery.createdBy || "System",
   createdAt,
   createdAtDisplay: createdAt
    ? moment(createdAt).format("DD-MMM-YYYY HH:mm")
    : "-",
   surgeryDate: scheduledDate,
   surgeryDateDisplay: scheduledDate
    ? moment(scheduledDate).format("DD-MMM-YYYY")
    : "-",
   scheduledTime: surgery.scheduledTime || "",
   patientAge,
   patientGender,
   eye,
   diet,
   lens,
   specialNote: surgery.specialInstructions || surgery.preOpInstructions || "-",
   surgeryName,
   surgeryGroup,
   remarks,
  };
 };

 computeSummary = (rows) => {
  const grouped = rows.reduce((acc, row) => {
   const key = row.createdByName || "Unknown";
   acc[key] = (acc[key] || 0) + 1;
   return acc;
  }, {});
  return Object.entries(grouped)
   .map(([user, count]) => ({ user, count }))
   .sort((a, b) => b.count - a.count);
 };

 generateReport = async () => {
  if (!this.state.fromDate || !this.state.toDate) {
   return;
  }
  try {
   this.setState({ isGenerating: true, hasGenerated: true });
   const payload = {
    page: 1,
    sortBy: "createdAt",
    sortOrder: "asc",
    startDate: moment(this.state.fromDate).format("YYYY-MM-DD"),
    endDate: moment(this.state.toDate).format("YYYY-MM-DD"),
    typeOfDate: "createdAt",
   };
   if (this.state.selectedBranch) {
    payload.branchId = this.state.selectedBranch;
    const branchName =
     this.state.selectedBranchName ||
     this.state.selectedBranch ||
     (this.state.branches.find((b) => b._id === this.state.selectedBranch)
      ?.name ??
      "");
    if (branchName) {
     payload.branchName = branchName;
    }
   }
   const response = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`,
    payload
   );
   const surgeries = response?.data?.items || response?.data || response || [];
   const rows = surgeries.map(this.mapSurgeryToRow).sort((a, b) => {
    const aTime = a.createdAt ? a.createdAt.getTime() : 0;
    const bTime = b.createdAt ? b.createdAt.getTime() : 0;
    return aTime - bTime;
   });

   // Store all rows for client-side filtering
   this.setState(
    {
     allRows: rows,
    },
    () => {
     // Apply client-side filters after storing all data
     this.applyClientSideFilters();
    }
   );

   if (this.state.gridApi) {
    this.state.gridApi.deselectAll();
   }
  } catch (err) {
   console.error("Failed to generate booking given report", err);
   this.setState({ rows: [], summary: [], totalCount: 0 });
  } finally {
   this.setState({ isGenerating: false });
  }
 };

 getColumnDefs = () => [
  {
   headerName: "Doctor/Surgeon",
   field: "doctorName",
   width: 180,
   pinned: "left",
   filter: "agTextColumnFilter",
   valueFormatter: (params) => params.value || "-",
   cellStyle: { fontWeight: "600" },
  },
  {
   headerName: "Branch",
   field: "branchName",
   width: 150,
   filter: "agTextColumnFilter",
   valueFormatter: (params) => params.value || "-",
   cellStyle: { fontWeight: "500" },
  },
  {
   headerName: "Surgery ID",
   field: "docNo",
   width: 130,
   filter: "agTextColumnFilter",
   valueGetter: (params) =>
    params.data.docNo || params.data.bookingReference || "-",
   cellRenderer: (params) => {
    const value = params.data.docNo || params.data.bookingReference;
    if (!value || value === "-") return "-";

    const container = document.createElement("div");
    const link = document.createElement("span");

    link.innerHTML = value;
    link.style.color = "#137cbd";
    link.style.cursor = "pointer";
    link.style.textDecoration = "underline";
    link.style.fontWeight = "600";
    link.onclick = () => this.navigateToDetails(params.data);

    container.appendChild(link);
    return container;
   },
  },
  {
   headerName: "PID/Patient Name",
   field: "patientInfo",
   width: 220,
   filter: "agTextColumnFilter",
   valueGetter: (params) => {
    const pid = params.data.patientId || params.data.pid || "-";
    const name = params.data.patientName || "-";
    return `${pid}/${name}`;
   },
   cellRenderer: (params) => {
    const pid = params.data.patientId || params.data.pid || "-";
    const name = params.data.patientName || "-";
    return `
          <div>
            <div style="font-weight: 600; margin-bottom: 4px;">${pid}/${name}</div>
          </div>
        `;
   },
  },
  {
   headerName: "Surgery Date",
   field: "surgeryDate",
   width: 150,
   filter: "agDateColumnFilter",
   valueGetter: (params) =>
    params.data.surgeryDate
     ? moment(params.data.surgeryDate).format("DD/MM/YYYY")
     : "-",
   cellRenderer: (params) => {
    const date = params.data.surgeryDate
     ? moment(params.data.surgeryDate).format("DD/MM/YYYY")
     : "-";
    const time = params.data.scheduledTime || "";
    return `
          <div style="text-align: center;">
            <div style="font-weight: 600; margin-bottom: 2px;">${date}</div>
            ${
             time
              ? `<div style="font-size: 12px; color: #8A9BA8;">${time}</div>`
              : ""
            }
          </div>
        `;
   },
   cellStyle: { textAlign: "center", fontWeight: "500" },
  },
  {
   headerName: "Booked At",
   field: "createdAt",
   width: 170,
   filter: "agDateColumnFilter",
   valueGetter: (params) =>
    params.data.createdAt
     ? moment(params.data.createdAt).format("DD/MM/YYYY HH:mm")
     : "-",
   cellRenderer: (params) => {
    if (!params.data.createdAt) return "-";
    const date = moment(params.data.createdAt).format("DD/MM/YYYY");
    const time = moment(params.data.createdAt).format("HH:mm");
    return `
          <div style="text-align: center;">
            <div style="font-weight: 600; margin-bottom: 2px;">${date}</div>
            <div style="font-size: 12px; color: #8A9BA8;">${time}</div>
          </div>
        `;
   },
   cellStyle: { textAlign: "center", fontWeight: "500" },
  },
  {
   headerName: "Booked By",
   field: "createdByName",
   width: 150,
   filter: "agTextColumnFilter",
   valueFormatter: (params) => params.value || "System",
   cellStyle: { fontWeight: "500" },
  },
  {
   headerName: "Status",
   field: "status",
   width: 140,
   filter: "agSetColumnFilter",
  filterValueGetter: (params) => this.getDisplayStatusMeta(params.data || {}).label,
  valueFormatter: (params) => this.getDisplayStatusMeta(params.data || {}).label,
  cellRenderer: (params) => this.renderStatusCell(params.data),
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "Is Billed",
   field: "isBilled",
   width: 120,
   filter: "agSetColumnFilter",
   valueGetter: (params) => getIsBilledLabel(params.data),
   valueFormatter: (params) => getIsBilledLabel(params.data),
   cellRenderer: (params) => this.renderIsBilledCell(params.data),
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "Age",
   field: "patientAge",
   width: 80,
   filter: "agNumberColumnFilter",
   cellStyle: { textAlign: "center", fontWeight: "500" },
  },
  {
   headerName: "Sex",
   field: "patientGender",
   width: 80,
   filter: "agTextColumnFilter",
   cellStyle: { textAlign: "center", fontWeight: "500" },
  },
  {
   headerName: "Eye",
   field: "eye",
   width: 90,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    const eyeColor =
     params.value === "RE"
      ? "#137CBD"
      : params.value === "LE"
      ? "#0F9960"
      : "#8F3985";
    const label = params.value || "-";
    return `<span style="background:${eyeColor}20;color:${eyeColor};padding:2px 8px;border-radius:12px;font-size:12px;font-weight:500;">${label}</span>`;
   },
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "Diet",
   field: "diet",
   width: 120,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    const isDiabetic = params.value === "DIABETIC";
    const color = isDiabetic ? "#D9822B" : "#0F9960";
    const label = params.value || "-";
    return `<span style="background:${color}20;color:${color};padding:2px 8px;border-radius:12px;font-size:12px;font-weight:500;">${label}</span>`;
   },
   cellStyle: { textAlign: "center" },
  },
  {
   headerName: "IOL/Implant",
   field: "lens",
   width: 260,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    const value = params.value || "NO LENS";
    const style =
     value === "NO LENS"
      ? "font-style: italic; color: #8A9BA8;"
      : "color: #F5F8FA;";
    return `<div style="${style} line-height: 1.4; word-wrap: break-word; max-width: 250px;">${value}</div>`;
   },
  },
  {
   headerName: "Surgery",
   field: "surgeryName",
   width: 280,
   filter: "agTextColumnFilter",
   cellRenderer: (params) => {
    const value = params.value || "-";
    return `<div style="line-height: 1.4; white-space: pre-wrap;">${value}</div>`;
   },
  },
  {
   headerName: "Surgery Group",
   field: "surgeryGroup",
   width: 200,
   filter: "agTextColumnFilter",
  },
  {
   headerName: "Remarks",
   field: "remarks",
   width: 220,
   filter: "agTextColumnFilter",
   cellRenderer: (params) =>
    `<div style="white-space: pre-wrap;">${params.value || "-"}</div>`,
  },
 ];

 renderFilters = () => {
  return (
   <Card
    elevation={2}
    style={{
     padding: "20px",
     backgroundColor: "var(--bg-surface)",
     border: "1px solid var(--border-color)",
     marginBottom: "20px",
    }}
   >
    <div
     style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}
    >
     <Icon
      icon="filter"
      style={{ marginRight: "10px", color: "var(--text-primary)" }}
     />
     <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Filters</H3>
     <div
      style={{
       marginLeft: "auto",
       display: "flex",
       gap: "10px",
       alignItems: "center",
      }}
     >
      <Tag intent="primary" minimal>
       {this.state.totalCount} bookings
      </Tag>
      <InputGroup
       placeholder="Quick filter"
       value={this.state.quickFilterText}
       onChange={this.onQuickFilterChanged}
       leftIcon="search"
       style={{ width: "220px" }}
      />
     </div>
    </div>

    <div
     style={{
      display: "flex",
      gap: "18px",
      flexWrap: "wrap",
      alignItems: "center",
     }}
    >
     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal>From</Tag>
      <DateInput
       value={this.state.fromDate}
       onChange={(date) => this.handleDateChange("fromDate", date)}
       formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
       parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
       placeholder="Select date"
       rightElement={<Icon icon="calendar" size={14} />}
       popoverProps={{ minimal: true }}
      />
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal>To</Tag>
      <DateInput
       value={this.state.toDate}
       onChange={(date) => this.handleDateChange("toDate", date)}
       formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
       parseDate={(str) => moment(str, "DD-MMM-YYYY").toDate()}
       placeholder="Select date"
       rightElement={<Icon icon="calendar" size={14} />}
       popoverProps={{ minimal: true }}
      />
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal>Branch</Tag>
      <div style={{ width: "200px" }}>
       <SingleSelectWrapper
        items={[
         `All Branches (${this.state.branches.length})`,
         ...this.createBranchSelectItems(),
        ]}
        selectedItem={
         this.state.selectedBranchName ||
         `All Branches (${this.state.branches.length})`
        }
        placeholder="Choose branch"
        onChange={(value) => {
         if (value === `All Branches (${this.state.branches.length})`) {
          this.handleBranchFilterSelect(null);
         } else {
          this.handleBranchFilterSelect(value);
         }
        }}
        width="100%"
       />
      </div>
     </div>

     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Tag minimal>Doctor</Tag>
      <div style={{ width: "200px" }}>
       <SingleSelectWrapper
        items={[
         `All Doctors (${this.state.doctors.length})`,
         ...this.createDoctorSelectItems(),
        ]}
        selectedItem={
         this.state.selectedDoctorName ||
         `All Doctors (${this.state.doctors.length})`
        }
        placeholder="Search doctors..."
        onChange={(value) => {
         if (value === `All Doctors (${this.state.doctors.length})`) {
          this.handleDoctorFilterSelect(null);
         } else {
          this.handleDoctorFilterSelect(value);
         }
        }}
        width="100%"
       />
      </div>
     </div>

     <Button
      icon="play"
      intent="primary"
      onClick={this.generateReport}
      loading={this.state.isGenerating}
      disabled={
       !this.state.fromDate || !this.state.toDate || this.state.isGenerating
      }
     >
      Generate
     </Button>

     <Button
      icon="document"
      onClick={this.exportToPDF}
      disabled={this.state.rows.length === 0}
      title="Export to PDF"
     >
      PDF
     </Button>

     <Button
      icon="th"
      onClick={this.exportToCSV}
      disabled={this.state.rows.length === 0}
      title="Export to CSV"
     >
      CSV
     </Button>
    </div>
   </Card>
  );
 };

 renderGrid = () => {
  if (this.state.isGenerating || this.state.isLoading) {
   return (
    <NonIdealState
     icon={<Spinner />}
     title="Generating report"
     description="Please wait"
    />
   );
  }

  if (this.state.rows.length === 0) {
   return (
    <div
     style={{
      textAlign: "center",
      padding: "40px",
      color: "var(--text-secondary)",
      backgroundColor: "var(--bg-secondary)",
      borderRadius: "8px",
      border: "1px solid var(--border-color)",
     }}
    >
     <Icon
      icon="calendar"
      size={48}
      style={{ marginBottom: "15px", opacity: 0.5 }}
     />
     <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>
      No bookings found
     </div>
     <div style={{ fontSize: "14px", opacity: 0.8 }}>
      Try adjusting your date range or filters
     </div>
    </div>
   );
  }

  return (
   <div
    className="ag-theme-alpine-dark"
    style={{
     height: "520px",
     width: "100%",
     borderRadius: "8px",
     border: "1px solid var(--border-color)",
     backgroundColor: "var(--bg-surface)",
    }}
   >
    <AgGridReact
     rowData={this.state.rows}
     columnDefs={this.getColumnDefs()}
     onGridReady={this.onGridReady}
     quickFilterText={this.state.quickFilterText}
     defaultColDef={{
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 120,
     }}
     sideBar={{
      toolPanels: [
       {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
         suppressRowGroups: true,
         suppressValues: true,
         suppressPivots: true,
         suppressPivotMode: true,
         suppressColumnFilter: false,
         suppressColumnSelectAll: false,
         suppressColumnExpandAll: false,
        },
       },
      ],
      defaultToolPanel: "",
     }}
     pagination
     paginationPageSize={50}
     paginationPageSizeSelector={[25, 50, 100]}
     rowHeight={48}
     headerHeight={42}
     animateRows
     suppressRowClickSelection
     rowSelection="multiple"
    />
   </div>
  );
 };

 renderSummary = () => {
  if (!this.state.rows.length) {
   return null;
  }
  return (
   <Card
    elevation={1}
    style={{
     marginTop: "20px",
     padding: "16px",
     backgroundColor: "var(--bg-surface)",
     border: "1px solid var(--border-color)",
    }}
   >
    <H3
     style={{
      marginTop: 0,
      marginBottom: "12px",
      color: "var(--text-primary)",
     }}
    >
     Booking Count by User
    </H3>
    <div
     style={{
      overflowX: "auto",
      border: "1px solid var(--border-color)",
      borderRadius: "6px",
     }}
    >
     <table
      style={{
       width: "100%",
       borderCollapse: "collapse",
       fontSize: "14px",
       backgroundColor: "var(--bg-primary)",
      }}
     >
      <thead>
       <tr style={{ backgroundColor: "var(--bg-secondary)" }}>
        <th
         style={{
          padding: "10px",
          textAlign: "left",
          border: "1px solid var(--border-color)",
         }}
        >
         User
        </th>
        <th
         style={{
          padding: "10px",
          textAlign: "right",
          border: "1px solid var(--border-color)",
         }}
        >
         Bookings
        </th>
       </tr>
      </thead>
      <tbody>
       {this.state.summary.map((row) => (
        <tr key={row.user}>
         <td
          style={{ padding: "10px", border: "1px solid var(--border-color)" }}
         >
          {row.user}
         </td>
         <td
          style={{
           padding: "10px",
           border: "1px solid var(--border-color)",
           textAlign: "right",
          }}
         >
          {row.count}
         </td>
        </tr>
       ))}
       <tr style={{ backgroundColor: "var(--bg-secondary)" }}>
        <td
         style={{
          padding: "10px",
          border: "1px solid var(--border-color)",
          fontWeight: 600,
         }}
        >
         Total
        </td>
        <td
         style={{
          padding: "10px",
          border: "1px solid var(--border-color)",
          fontWeight: 600,
          textAlign: "right",
         }}
        >
         {this.state.totalCount}
        </td>
       </tr>
      </tbody>
     </table>
    </div>
   </Card>
  );
 };

 render() {
  return (
   <div
    style={{
     padding: "20px",
     backgroundColor: "var(--bg-primary)",
     minHeight: "100vh",
     color: "var(--text-primary)",
    }}
   >
    <div
     style={{ marginBottom: "20px", display: "flex", alignItems: "center" }}
    >
     <Icon
      icon="timeline-events"
      style={{ marginRight: "12px", color: "var(--intent-primary)" }}
     />
     <H1 style={{ margin: 0, color: "var(--text-primary)" }}>Booking Given</H1>
    </div>

    {this.renderFilters()}
    {this.renderGrid()}
    {this.renderSummary()}
   </div>
  );
 }
}

export default withRouter(inject("authStore")(observer(BookingGivenReport)));
